/**
 * library-dialogs.tsx — Hộp thoại của Thư mục dùng chung: sửa tài liệu (quy tắc an toàn chung / mẫu), chọn phạm vi
 * "Áp dụng cho" của kỹ năng, thêm / sửa kỹ năng.
 */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Loader2, Save, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Switch, Textarea } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { createScenario, deleteScenario, fetchScenario, updateScenario, type AiBot, type ScenarioMeta } from '@/hooks/use-ai'
import { invalidateLibrary, useSaveLibraryDoc, useSetScenarioBots } from '@/hooks/use-ai-khung'

// ── Sửa một tài liệu ─────────────────────────────────────────────────

export function LibraryDocDialog({
  type, title, note, initial, canEdit, onClose,
}: { type: string; title: string; note: string; initial: string | null; canEdit: boolean; onClose: () => void }) {
  const { t } = useTranslation('aiOps')
  const [content, setContent] = useState(initial ?? '')
  const save = useSaveLibraryDoc()
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{note}</DialogDescription>
        </DialogHeader>
        <textarea
          className="min-h-[360px] w-full resize-y rounded-md border bg-transparent p-3 font-mono text-xs outline-none focus:ring-1 focus:ring-ring"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={!canEdit}
          aria-label={title}
          placeholder={t('libraryDialogs.docEmpty')}
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t('common:actions.close')}</Button>
          {canEdit && (
            <Button
              disabled={save.isPending}
              onClick={() => save.mutate({ type, content }, {
                onSuccess: () => { toast.success(t('common:status.saved')); onClose() },
                onError: (err) => toast.error(apiError(err)),
              })}
            >
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t('common:actions.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Chọn phạm vi "Áp dụng cho" ───────────────────────────────────────

/** null = mọi nhân viên AI; mảng = các nhân viên được chọn. */
export function ScopePicker({
  value, onChange, bots, disabled,
}: { value: string[] | null; onChange: (v: string[] | null) => void; bots: Array<Pick<AiBot, 'id' | 'name'>>; disabled?: boolean }) {
  const { t } = useTranslation('aiOps')
  const all = value === null
  const selected = new Set(value ?? [])
  const toggle = (id: string, on: boolean) => {
    const next = new Set(selected)
    if (on) next.add(id)
    else next.delete(id)
    onChange([...next])
  }
  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t('libraryDialogs.scope.label')}>
        {[
          { id: 'all', title: t('libraryDialogs.scope.allTitle'), desc: t('libraryDialogs.scope.allDesc') },
          { id: 'some', title: t('libraryDialogs.scope.someTitle'), desc: t('libraryDialogs.scope.someDesc') },
        ].map((o) => {
          const on = o.id === 'all' ? all : !all
          return (
            <button
              key={o.id} type="button" role="radio" aria-checked={on} disabled={disabled}
              onClick={() => onChange(o.id === 'all' ? null : [...selected])}
              className={cn('rounded-lg border p-2.5 text-left transition-colors disabled:opacity-60', on ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50')}
            >
              <span className="block text-sm font-medium">{o.title}</span>
              <span className="block text-xs text-muted-foreground">{o.desc}</span>
            </button>
          )
        })}
      </div>
      {!all && (
        bots.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('libraryDialogs.scope.noBots')}</p>
        ) : (
          <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border p-2">
            {bots.map((b) => (
              <label key={b.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/50">
                <Checkbox checked={selected.has(b.id)} onCheckedChange={(v) => toggle(b.id, v === true)} disabled={disabled} />
                <span className="truncate">{b.name}</span>
              </label>
            ))}
          </div>
        )
      )}
      {!all && selected.size === 0 && (
        <p className="text-xs text-warning">{t('libraryDialogs.scope.noneSelected')}</p>
      )}
    </div>
  )
}

export function ScopeDialog({
  scenario, bots, canEdit, onClose,
}: { scenario: ScenarioMeta; bots: Array<Pick<AiBot, 'id' | 'name'>>; canEdit: boolean; onClose: () => void }) {
  const { t } = useTranslation('aiOps')
  const [value, setValue] = useState<string[] | null>(scenario.botIds === undefined ? null : scenario.botIds)
  const save = useSetScenarioBots()
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('libraryDialogs.scopeDialog.title', { name: scenario.name })}</DialogTitle>
          <DialogDescription>{t('libraryDialogs.scopeDialog.desc')}</DialogDescription>
        </DialogHeader>
        <ScopePicker value={value} onChange={setValue} bots={bots} disabled={!canEdit} />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
          {canEdit && (
            <Button
              disabled={save.isPending}
              onClick={() => save.mutate({ id: scenario.id, botIds: value }, {
                onSuccess: () => { toast.success(t('libraryDialogs.scopeDialog.saved')); onClose() },
                onError: (err) => toast.error(apiError(err)),
              })}
            >
              {t('common:actions.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Thêm / sửa kỹ năng ───────────────────────────────────────────────

interface SkillForm {
  id: string | null
  name: string
  description: string
  content: string
  triggerHints: string
  loadMode: string
  enabled: boolean
  botIds: string[] | null
}

const COND_MAX = 500
const INSTR_MAX = 2000

export function SkillDialog({
  scenario, bots, canEdit, onClose,
}: { scenario: ScenarioMeta | null; bots: Array<Pick<AiBot, 'id' | 'name'>>; canEdit: boolean; onClose: () => void }) {
  const { t } = useTranslation('aiOps')
  const qc = useQueryClient()
  // Kỹ năng mới: mặc định CHƯA áp nhân viên AI nào (phải chọn) — khung ba tầng Q3.
  const [f, setF] = useState<SkillForm | null>(
    scenario ? null : { id: null, name: '', description: '', content: '', triggerHints: '', loadMode: 'auto', enabled: true, botIds: [] },
  )
  const [busy, setBusy] = useState(false)
  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose }, [onClose])

  // Nạp nội dung đầy đủ một lần khi mở (danh sách chỉ có phần tóm tắt; danh sách tải lại không đè bản đang sửa).
  const [opened] = useState(scenario)
  useEffect(() => {
    if (!opened) return
    let alive = true
    fetchScenario(opened.id)
      .then(({ scenario: s }) => {
        if (!alive) return
        setF({
          id: s.id, name: s.name, description: s.description, content: s.content, triggerHints: s.triggerHints ?? '',
          loadMode: s.loadMode, enabled: s.enabled, botIds: s.botIds === undefined ? (opened.botIds ?? null) : s.botIds,
        })
      })
      .catch((err) => { toast.error(apiError(err)); closeRef.current() })
    return () => { alive = false }
  }, [opened])

  const save = async () => {
    if (!f) return
    if (!f.name.trim()) return void toast.error(t('libraryDialogs.skill.nameRequired'))
    if (!f.description.trim()) return void toast.error(t('libraryDialogs.skill.whenRequired'))
    if (!f.content.trim()) return void toast.error(t('libraryDialogs.skill.instrRequired'))
    setBusy(true)
    try {
      const body = {
        name: f.name.trim(), description: f.description.trim(), content: f.content, triggerHints: f.triggerHints || null,
        loadMode: f.loadMode, enabled: f.enabled, botIds: f.botIds,
      }
      if (f.id) await updateScenario(f.id, body)
      else await createScenario(body)
      await invalidateLibrary(qc)
      if (f.botIds && f.botIds.length === 0) toast.warning(t('libraryDialogs.skill.savedNoBots'), { description: t('libraryDialogs.skill.savedNoBotsHint') })
      else toast.success(t('libraryDialogs.skill.saved'))
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!f?.id || !window.confirm(t('libraryDialogs.skill.confirmDelete', { name: f.name }))) return
    try {
      await deleteScenario(f.id)
      await invalidateLibrary(qc)
      toast.success(t('libraryDialogs.skill.deleted'))
      onClose()
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{scenario ? t('libraryDialogs.skill.editTitle') : t('libraryDialogs.skill.addTitle')}</DialogTitle>
          <DialogDescription>{t('libraryDialogs.skill.desc')}</DialogDescription>
        </DialogHeader>
        {!f ? (
          <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('libraryDialogs.skill.loading')}</div>
        ) : (
          <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
              <div>
                <Label className="text-xs">{t('libraryDialogs.skill.nameLabel')}</Label>
                <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={t('libraryDialogs.skill.namePlaceholder')} disabled={!canEdit} />
              </div>
              <div>
                <Label className="text-xs">{t('libraryDialogs.skill.loadModeLabel')}</Label>
                <Select value={f.loadMode} onValueChange={(v) => setF({ ...f, loadMode: v })} disabled={!canEdit}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t('libraryDialogs.skill.loadAuto')}</SelectItem>
                    <SelectItem value="always">{t('libraryDialogs.skill.loadAlways')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">{t('libraryDialogs.skill.whenLabel')} <span className="font-normal text-muted-foreground">{t('libraryDialogs.skill.whenHint')}</span></Label>
              <Textarea rows={2} maxLength={COND_MAX} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder={t('libraryDialogs.skill.whenPlaceholder')} disabled={!canEdit} />
            </div>
            <div>
              <Label className="text-xs">{t('libraryDialogs.skill.instrLabel')}</Label>
              <textarea
                className="min-h-[160px] w-full resize-y rounded-md border bg-transparent p-3 font-mono text-xs outline-none focus:ring-1 focus:ring-ring"
                maxLength={INSTR_MAX}
                value={f.content}
                onChange={(e) => setF({ ...f, content: e.target.value })}
                placeholder={t('libraryDialogs.skill.instrPlaceholder')}
                disabled={!canEdit}
              />
              <p className="text-right text-[11px] text-muted-foreground">{f.content.length}/{INSTR_MAX}</p>
            </div>
            <div>
              <Label className="text-xs">{t('libraryDialogs.skill.hintsLabel')}</Label>
              <Input value={f.triggerHints} onChange={(e) => setF({ ...f, triggerHints: e.target.value })} placeholder={t('libraryDialogs.skill.hintsPlaceholder')} disabled={!canEdit} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('libraryDialogs.scope.label')}</Label>
              <ScopePicker value={f.botIds} onChange={(v) => setF({ ...f, botIds: v })} bots={bots} disabled={!canEdit} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} disabled={!canEdit} /> {t('common:status.enabled')}
            </label>
          </div>
        )}
        <DialogFooter className="gap-2">
          {f?.id && canEdit && (
            <Button variant="ghost" className="mr-auto text-destructive" onClick={() => void remove()}>
              <Trash2 className="h-4 w-4" /> {t('common:actions.delete')}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
          {canEdit && <Button onClick={() => void save()} disabled={busy || !f}>{busy ? t('common:status.saving') : t('common:actions.save')}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
